import { convertToWav, isALACEncodedM4A, convertM4AToAAC } from '@/components/voice/AudioVoiceRecorder/components/utils';
import { getHummingAudio, postAudioUploadForAcr, UpdateFileTypeEnum } from '@/service/generatorUpload';
import useGeneratorStore, { R3ImageTypeEnum, R3imagineStatusType, RecordingStatusEnum } from '@/state/generator-store';
import { convertMillisecondsToSeconds, convertSecondsToMilliseconds } from '@/utils/time';
import { useRequest } from 'ahooks';
import { default as React, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AUDIO_CONSTRAINTS, FILE_MESSAGES } from './constants';
import { AudioFile, AudioStatus } from './types';

const ALLOWED_AUDIO_TYPES = ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/x-m4a', 'audio/m4a'] as const;
const ALLOWED_AUDIO_EXTENSIONS = ['.mp3', '.wav', '.m4a'] as const;
type AllowedAudioType = (typeof ALLOWED_AUDIO_TYPES)[number];
type AllowedAudioExtension = (typeof ALLOWED_AUDIO_EXTENSIONS)[number];

const isValidAudioFile = (file: File): boolean => {
  const fileExtension = file.name.toLowerCase().match(/\.[^.]*$/)?.[0] as AllowedAudioExtension;
  return ALLOWED_AUDIO_TYPES.includes(file.type as AllowedAudioType) || ALLOWED_AUDIO_EXTENSIONS.includes(fileExtension);
};

export const useAudioValidation = () => {
  const validateAudioFile = (duration: number, size: number): boolean => {
    if (duration < AUDIO_CONSTRAINTS.DURATION.MIN) {
      toast.error(FILE_MESSAGES.DURATION_MIN);
      return false;
    }
    if (duration > AUDIO_CONSTRAINTS.DURATION.MAX) {
      toast.error(FILE_MESSAGES.DURATION_MAX);
      return false;
    }
    if (size > AUDIO_CONSTRAINTS.SIZE.MAX) {
      toast.error(FILE_MESSAGES.SIZE_MAX(10));
      return false;
    }
    return true;
  };

  return { validateAudioFile };
};

export function useModel() {
  const { validateAudioFile } = useAudioValidation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    setHummingAudioSelected,
    setR3ImageStatus,
    setR3ImageType,
    setUploadFileData,
    currentRecordFile,
    setCurrentRecordFile,
    setRecordingStatus,
  } = useGeneratorStore();

  const [isOpenHummingAudioList, setIsOpenHummingAudioList] = useState<boolean>(false);

  const createAndSetAudioFile = (file: File | Blob, fileName: string, duration: number, status: AudioStatus = 'uploading'): void => {
    if (!validateAudioFile(duration, file.size)) return;

    const audioFile: AudioFile = {
      file,
      name: fileName,
      size: file.size,
      status,
      errorMessage: null,
      src: URL.createObjectURL(file),
      duration,
      type: UpdateFileTypeEnum.record,
    };

    setCurrentRecordFile(audioFile);
    setRecordingStatus(RecordingStatusEnum.RecordingFinished);
  };

  const processAudioFile = (file: File | Blob, duration: number, fileName: string) => {
    createAndSetAudioFile(file, fileName, duration);
  };

  const handleRecordingComplete = async (audioBlob: Blob, duration: number) => {
    if (!validateAudioFile(duration, audioBlob.size)) return;
    const waveFile = await convertToWav(audioBlob);
    createAndSetAudioFile(waveFile, 'Recorded Audio.wav', duration);
    setR3ImageType(R3ImageTypeEnum.record);
  };

  const { data: hummingAudioData, run: getHummingAudioRun } = useRequest(getHummingAudio, {
    cacheKey: 'getHummingAudio',
    staleTime: 500,
  });

  const { run: postAudioUploadForAcrRun } = useRequest(postAudioUploadForAcr, {
    manual: true,
    onSuccess: (res) => {
      setHummingAudioSelected(null);
      setR3ImageStatus(R3imagineStatusType.WaitingAgree);
      setUploadFileData(res);
      toast.success(FILE_MESSAGES.UPLOAD_SUCCESS.TITLE, {
        description: FILE_MESSAGES.UPLOAD_SUCCESS.DESCRIPTION,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? 'Unknown Error.');
    },
  });

  const uploadRecordingFile = () => {
    const formData = new FormData();
    const { name: fileName, file, duration } = currentRecordFile;
    formData.append('file', file, fileName);
    formData.append('duration', String(convertMillisecondsToSeconds(duration)));
    postAudioUploadForAcrRun(formData);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0] as File;
      if (!isValidAudioFile(file)) {
        toast.error(FILE_MESSAGES.INVALID_FILE_TYPE);
        return;
      }

      const isM4aFile = file.type === 'audio/x-m4a' || file.type === 'audio/m4a' || file.name.toLowerCase().endsWith('.m4a');

      if (isM4aFile) {
        try {
          const isALAC = await isALACEncodedM4A(file);
          let processedFile = file;

          if (isALAC) {
            processedFile = await convertM4AToAAC(file);
          }

          const audio = document.createElement('audio');
          audio.src = URL.createObjectURL(processedFile);
          audio.load();

          audio.onerror = (e) => {
            toast.error(FILE_MESSAGES.INVALID_FILE_FORMAT);
            URL.revokeObjectURL(audio.src);
          };

          audio.onloadedmetadata = () => {
            const duration = Math.round(convertSecondsToMilliseconds(audio.duration));
            processAudioFile(processedFile, duration, file.name);
            URL.revokeObjectURL(audio.src);
          };
        } catch (error) {
          console.error('Error processing m4a file:', error);
          toast.error('Error processing audio file');
        }
        return;
      }

      const audio = document.createElement('audio');
      audio.src = URL.createObjectURL(file);
      audio.load();

      audio.onerror = (e) => {
        toast.error(FILE_MESSAGES.INVALID_FILE_FORMAT);
        URL.revokeObjectURL(audio.src);
      };

      audio.onloadedmetadata = () => {
        const duration = Math.round(convertSecondsToMilliseconds(audio.duration));
        processAudioFile(file, duration, file.name);
        URL.revokeObjectURL(audio.src);
      };
    }
  };

  const handleClickUpload = () => {
    const inputElement = fileInputRef.current;
    if (inputElement) {
      inputElement.click();
    }
  };

  return {
    fileInputRef,
    handleUpload,
    handleClickUpload,
    hummingAudioData,
    isOpenHummingAudioList,
    setIsOpenHummingAudioList,
    handleRecordingComplete,
    getHummingAudioRun,
    uploadRecordingFile,
  };
}
